import { supabase } from '../supabase';
import { getCallStatusKey } from '../utils/callStatus';

/**
 * Get user call statistics
 */
export const getUserCallStats = async (userId) => {
  // Ensure userId is a string for compatibility
  const userIdStr = String(userId);
  
  const { data, error } = await supabase
    .from('user_calls')
    .select('*')
    .eq('user_id', userIdStr)
    .maybeSingle();

  if (error) throw error;

  // No record yet — return defaults (avoids 406 when using .single() with 0 rows)
  if (!data) {
    return {
      user_id: userIdStr,
      minutes_limit: 600,
      calls_duration: 0,
    };
  }

  return data;
};

/**
 * Create or update call statistics
 */
export const updateCallStats = async (userId, minutesUsed) => {
  const userIdStr = String(userId);
  const currentStats = await getUserCallStats(userId);
  const newDuration = (currentStats.calls_duration || 0) + minutesUsed;

  const { data, error } = await supabase
    .from('user_calls')
    .upsert({
      user_id: userIdStr,
      minutes_limit: currentStats.minutes_limit,
      calls_duration: newDuration,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Reset call duration (for monthly reset)
 */
export const resetCallDuration = async (userId) => {
  const userIdStr = String(userId);
  
  const { data, error } = await supabase
    .from('user_calls')
    .update({
      calls_duration: 0,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userIdStr)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Update call limit
 */
export const updateCallLimit = async (userId, newLimit) => {
  const userIdStr = String(userId);
  
  const { data, error } = await supabase
    .from('user_calls')
    .upsert({
      user_id: userIdStr,
      minutes_limit: newLimit,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

const mapUserCallRows = (calls, candidateById) =>
  (calls ?? []).map((call) => ({
    id: call.id,
    created_at: call.created_at,
    summary: call.ai_summary?.trim?.() || 'No summary available.',
    status: call.status?.trim?.() || null,
    callDuration: call.call_duration ?? null,
    candidate: candidateById[String(call.candidate_id)] ?? null,
    candidateId: call.candidate_id,
  }));

const fetchUserCallRows = async (userId, { limit } = {}) => {
  const userIdStr = String(userId);

  const { data: candidates, error: candidatesError } = await supabase
    .from('candidates')
    .select('id, full_name, avatar, current_position')
    .eq('user_id', userIdStr);

  if (candidatesError) throw candidatesError;
  if (!candidates?.length) return [];

  const candidateById = Object.fromEntries(candidates.map((c) => [String(c.id), c]));
  const candidateIds = candidates.map((c) => c.id);

  let query = supabase
    .from('candidates_calls')
    .select('id, created_at, ai_summary, status, call_duration, candidate_id')
    .in('candidate_id', candidateIds)
    .order('created_at', { ascending: false });

  if (limit != null) {
    query = query.limit(limit);
  }

  const { data: calls, error: callsError } = await query;
  if (callsError) throw callsError;

  return mapUserCallRows(calls, candidateById);
};

/**
 * Get all AI calls for a user's candidates (newest first)
 */
export const getUserCalls = async (userId, { limit } = {}) =>
  fetchUserCallRows(userId, { limit: limit ?? undefined });

/**
 * Get the most recent AI call per candidate (newest first)
 */
export const getUserLatestCallsPerCandidate = async (userId) => {
  const calls = await fetchUserCallRows(userId);
  const seen = new Set();
  const latest = [];

  calls.forEach((call) => {
    const candidateKey = String(call.candidateId);
    if (seen.has(candidateKey)) return;
    seen.add(candidateKey);
    latest.push(call);
  });

  return latest;
};

/**
 * Get all AI calls for a candidate (newest first)
 */
export const getCandidateCalls = async (candidateId) => {
  const { data, error } = await supabase
    .from('candidates_calls')
    .select('id, created_at, ai_summary, status, call_duration')
    .eq('candidate_id', candidateId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
};

/**
 * Get the most recent call for a candidate
 */
export const getLastCandidateCall = async (candidateId) => {
  const calls = await getCandidateCalls(candidateId);
  return calls[0] ?? null;
};

/**
 * Get total AI calls count for a user's candidates
 */
export const getUserCallsCount = async (userId, { since } = {}) => {
  const stats = await getUserCallActivityStats(userId, { since });
  return stats.total;
};

/**
 * Call activity counters for overview cards (optionally filtered by created_at >= since).
 */
export const getUserCallActivityStats = async (userId, { since } = {}) => {
  const empty = { total: 0, noResponse: 0, openToMeet: 0, scheduledInterviews: 0 };
  const userIdStr = String(userId);

  const { data: candidates, error: candidatesError } = await supabase
    .from('candidates')
    .select('id')
    .eq('user_id', userIdStr);

  if (candidatesError) throw candidatesError;
  if (!candidates?.length) return empty;

  const candidateIds = candidates.map((candidate) => candidate.id);

  let query = supabase
    .from('candidates_calls')
    .select('id, status')
    .in('candidate_id', candidateIds);

  if (since) {
    query = query.gte('created_at', since.toISOString());
  }

  const { data: calls, error } = await query;
  if (error) throw error;

  return (calls ?? []).reduce((acc, call) => {
    acc.total += 1;
    const key = getCallStatusKey(call.status);
    if (key === 'no response') acc.noResponse += 1;
    if (key === 'open to meet') acc.openToMeet += 1;
    if (key === 'scheduled interview') acc.scheduledInterviews += 1;
    return acc;
  }, { ...empty });
};

/**
 * Get call usage percentage
 */
export const getCallUsagePercentage = async (userId) => {
  const stats = await getUserCallStats(userId);
  const percentage = (stats.calls_duration / stats.minutes_limit) * 100;
  
  return {
    used: stats.calls_duration,
    limit: stats.minutes_limit,
    percentage: Math.min(percentage, 100),
    remaining: Math.max(stats.minutes_limit - stats.calls_duration, 0),
  };
};
